import { C_BASE_RECIPES } from "./cBase";
import { buildCompletionSequence } from "./completion";
import { LOCKED_DEATH, LOCKED_JACKPOT, LOCKED_LEVEL_COMPLETE_CORE, LOCKED_SPEED_TRIAL, LOCKED_STARFRUIT, LOCKED_WORMHOLE } from "./lockedCandidates";
import { requireLayerByLabel, requireLayersByLabels, SOUND_EVENT_IDS } from "@/audio/core/types";
import type { CandidateLayer, LabeledLayerSource, SoundEventId } from "@/audio/core/types";

/**
 * Production's single authoritative lookup from every `SoundEventId` to its
 * approved final recipe - the production-owned twin of
 * `src/audio/lab/final/finalPalette.ts`, built from the exact same locked
 * candidates (`lockedCandidates.ts`) and C-base recipes (`cBase.ts`) that
 * live under `src/audio/catalog/`, never copied. `AudioManager` is the only
 * intended consumer; the QA lab keeps its own independent lookup so a bug
 * in one can never silently mask a bug in the other.
 */

const starfruitSource: LabeledLayerSource = { id: "starfruit-s3", layers: LOCKED_STARFRUIT };
const wormholeSource: LabeledLayerSource = { id: "wormhole-w2", layers: LOCKED_WORMHOLE };
const speedTrialSource: LabeledLayerSource = { id: "speed-trial-t1", layers: LOCKED_SPEED_TRIAL };

/** One authoritative final recipe for a `SoundEventId` - one or more layers, plus which locked family (if any) it came from. */
export interface FinalMappingEntry {
  readonly eventId: SoundEventId;
  readonly origin: "cBase" | "locked";
  readonly lockedFamily?: "jackpot" | "starfruit" | "wormhole" | "speedTrial" | "levelComplete" | "death";
  readonly layers: readonly CandidateLayer[];
}

function cBase(eventId: SoundEventId): FinalMappingEntry {
  return { eventId, origin: "cBase", layers: [{ atMs: 0, synth: C_BASE_RECIPES[eventId], label: eventId }] };
}

function lockedWhole(eventId: SoundEventId, family: NonNullable<FinalMappingEntry["lockedFamily"]>, layers: readonly CandidateLayer[]): FinalMappingEntry {
  return { eventId, origin: "locked", lockedFamily: family, layers };
}

function lockedLayer(eventId: SoundEventId, family: NonNullable<FinalMappingEntry["lockedFamily"]>, source: LabeledLayerSource, label: string): FinalMappingEntry {
  return { eventId, origin: "locked", lockedFamily: family, layers: [requireLayerByLabel(source, label)] };
}

function lockedLayers(eventId: SoundEventId, family: NonNullable<FinalMappingEntry["lockedFamily"]>, source: LabeledLayerSource, labels: readonly string[]): FinalMappingEntry {
  return { eventId, origin: "locked", lockedFamily: family, layers: requireLayersByLabels(source, labels) };
}

// The completion matrix's star-fill/record layers are shared archetype
// output scoped to the approved L3 core - see `completion.ts`.
const l3WithOneStarAndStarRecord = buildCompletionSequence(1, "starRecord");
const l3WithOneStarAndHighScore = buildCompletionSequence(1, "highScore");
const levelCompleteSource: LabeledLayerSource = { id: "level-complete-with-star-record", layers: l3WithOneStarAndStarRecord };
const levelCompleteHighScoreSource: LabeledLayerSource = { id: "level-complete-with-high-score", layers: l3WithOneStarAndHighScore };

export const FINAL_MAPPING: Readonly<Record<SoundEventId, FinalMappingEntry>> = {
  // Everyday actions - accepted C-base baseline, unchanged.
  safe: cBase("safe"),
  rush1: cBase("rush1"),
  rush2: cBase("rush2"),
  rush3: cBase("rush3"),
  rush4: cBase("rush4"),
  rush5: cBase("rush5"),
  buttonClick: cBase("buttonClick"),
  pause: cBase("pause"),
  resume: cBase("resume"),

  // CHAOS - JACKPOT is the only locked-candidate CHAOS event; the rest stay accepted C-base.
  chaosAnticipation: cBase("chaosAnticipation"),
  jackpot: lockedWhole("jackpot", "jackpot", LOCKED_JACKPOT),
  shrink: cBase("shrink"),
  legacyScoreHit: cBase("legacyScoreHit"),

  // WORMHOLE family (W2) - WORMHOLE BITE/CLUTCH fall back to accepted C-base.
  wormholeActivation: lockedLayer("wormholeActivation", "wormhole", wormholeSource, "Activation (suction)"),
  wormholeLoop: lockedLayer("wormholeLoop", "wormhole", wormholeSource, "Active loop (drifting)"),
  wormholeWrap: lockedLayers("wormholeWrap", "wormhole", wormholeSource, ["Wrap 1 (pulled left)", "Wrap 1 (emerges right)"]),
  wormholeRefresh: lockedLayer("wormholeRefresh", "wormhole", wormholeSource, "Refresh (suction)"),
  wormholeWarningPulse: lockedLayer("wormholeWarningPulse", "wormhole", wormholeSource, "Warning pulse 1"),
  wormholeExpiry: lockedLayer("wormholeExpiry", "wormhole", wormholeSource, "Expiry (low collapse)"),
  wormholeBite: cBase("wormholeBite"),
  wormholeClutch: cBase("wormholeClutch"),

  // SPEED TRIAL family (T1) - fully covered by the story candidate.
  speedTrialStart: lockedLayer("speedTrialStart", "speedTrial", speedTrialSource, "Ignition"),
  speedTrialLoop: lockedLayer("speedTrialLoop", "speedTrial", speedTrialSource, "Active loop (light wind)"),
  speedTrialWarningPulse: lockedLayer("speedTrialWarningPulse", "speedTrial", speedTrialSource, "Warning pulse 1"),
  speedTrialSuccess: lockedLayer("speedTrialSuccess", "speedTrial", speedTrialSource, "Success"),
  speedTrialExit: lockedLayer("speedTrialExit", "speedTrial", speedTrialSource, "Exit"),

  // STARFRUIT and gates (S3) - QA follow-up: appearance now has its own
  // dedicated "chime + shimmer" cue, distinct from eaten's stronger
  // "Key unlock impact" identity (see `LOCKED_STARFRUIT`'s own doc).
  // Permanent gate transition and the standalone gate-entry event fall
  // back to C-base.
  starfruitAppear: lockedLayers("starfruitAppear", "starfruit", starfruitSource, ["Appearance chime (twinkle)", "Appearance beacon (shimmer)"]),
  starfruitEaten: lockedLayer("starfruitEaten", "starfruit", starfruitSource, "Key unlock impact"),
  energyTravel: lockedLayer("energyTravel", "starfruit", starfruitSource, "Rising harmonic beam"),
  gateTempOpen: lockedLayer("gateTempOpen", "starfruit", starfruitSource, "Wide celestial gate opening"),
  starfruitGateWarning: lockedLayer("starfruitGateWarning", "starfruit", starfruitSource, "Warning 3"),
  gateTempClose: lockedLayer("gateTempClose", "starfruit", starfruitSource, "Gate closes"),
  gatePermanentOpen: cBase("gatePermanentOpen"),
  gateEntry: cBase("gateEntry"),

  // Skill and result events - LEVEL COMPLETE (L3) and DEATH (D2) are locked;
  // the rest of the skill events stay accepted C-base.
  maneuver: cBase("maneuver"),
  doubleDanger: cBase("doubleDanger"),
  flowIncrease: cBase("flowIncrease"),
  trickBite: cBase("trickBite"),
  clutchBite: cBase("clutchBite"),
  death: lockedWhole("death", "death", LOCKED_DEATH),
  levelComplete: lockedWhole("levelComplete", "levelComplete", LOCKED_LEVEL_COMPLETE_CORE),
  starFill: lockedLayer("starFill", "levelComplete", levelCompleteSource, "Star fill 1"),
  newStarRecord: lockedLayer("newStarRecord", "levelComplete", levelCompleteSource, "NEW STAR RECORD!"),
  newHighScore: lockedLayer("newHighScore", "levelComplete", levelCompleteHighScoreSource, "NEW HIGH SCORE!"),
};

/** Sanity check run at module load: every required event id has an entry. Throws immediately rather than letting a gap surface only in a test run. */
for (const eventId of SOUND_EVENT_IDS) {
  if (!FINAL_MAPPING[eventId]) throw new Error(`FINAL_MAPPING is missing an entry for "${eventId}"`);
}
